import { AxiosResponse } from "axios";
import axiosInstance from "../utils/axios";
import { CarbonClickTheme } from "../@types/carbonClickTheme";
import { FormValuesProps } from "src/@types/sustainabilityPage";
import { MerchantTheme } from "src/@types/cms";

const BASE_URL = "/cms/v1";

async function sendTestEmailApi(data: any) {
  return axiosInstance.post(`${BASE_URL}/send-test-purchase-completed-email`, JSON.stringify(data))
}

async function getActiveThemeForMerchantApi(merchantCode: string) {
  return axiosInstance.get(`${BASE_URL}/themes/active`, {
    params: { merchantCode }
  })
}

async function getActivePortalConfigForMerchantApi(merchantCode: string) {
  return axiosInstance.get(`${BASE_URL}/components/merchant-portal`, {
    params: { merchantCode }
  })
}

async function getForMerchantThemeApi(merchantCode: string): Promise<AxiosResponse<MerchantTheme>> {
  return axiosInstance.get(`${BASE_URL}/components/transaction-details`, {
    params: { merchantCode }
  })
}

const saveThemeApi = async (theme: CarbonClickTheme) => {
  return axiosInstance.post(`${BASE_URL}/themes/`, JSON.stringify(theme))
}

const connectMerchantToThemeApi = async (merchantCode: string, themeId: string) => {
  return axiosInstance.post(`${BASE_URL}/themes/${themeId}/merchant/${merchantCode}`)
}

async function saveThemeForMerchantApi(merchantCode: string, theme: CarbonClickTheme) {
  const themeResponse = await saveThemeApi(theme);
  const themeId = themeResponse.data.id;
  return connectMerchantToThemeApi(merchantCode, themeId)
}

async function saveImageForMerchantApi(merchantCode: string, file: File, imageType?: string, configType?: string) {
  const formData = new FormData();
  formData.append('file', file);
  const config = {
    params: { imageType },
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  };

  const configTypeParam = configType ? `/${configType}` : '';
  return axiosInstance.post(`${BASE_URL}/images/merchant/${merchantCode}${configTypeParam}`, formData, config)
}

async function getListOfMerchantLandingPages(merchantCode: string) {
  return axiosInstance
    .get(`${BASE_URL}/component-settings/landing-pages?merchantCode=${merchantCode}`);
}

async function getDefaultComponentSettings(pageType: string, merchantName: string) {
  return axiosInstance.get<FormValuesProps>(`${BASE_URL}/component-settings/landing-pages/default`, {
    params: { type: pageType, merchantName }
  });
}

const sendComponentSettingsApi = async (componentSettings: FormValuesProps) => {
  return axiosInstance.post(`${BASE_URL}/component-settings/landing-pages`, JSON.stringify(componentSettings));
}

async function saveComponentSettingsApi(merchantCode: string, componentSettings: FormValuesProps) {
  const generalSettings = componentSettings?.generalSettings;
    const newSubdomain = generalSettings?.subdomain;
    delete generalSettings.subdomain;

    const componentSettingsApiResponse = await sendComponentSettingsApi(componentSettings);
    const componentSettingsId = componentSettingsApiResponse.data.id;
    const body = { subdomain: newSubdomain }

    await axiosInstance.post<FormValuesProps>(`${BASE_URL}/component-settings/landing-pages/${componentSettingsId}/merchant/${merchantCode}`, body);
    return componentSettingsApiResponse;
}

async function getSettingsById(componentConfigId: string) {
  return axiosInstance.get<FormValuesProps>(`${BASE_URL}/component-settings/landing-pages/${componentConfigId}`);
}

async function updateComponentSettingsApi(
  merchantCode: string,
  landingPageId: string | undefined,
  componentConfigId: string,
  componentSettings: FormValuesProps,
  status: boolean,
  domain?: string
) {
  const generalSettings = componentSettings?.generalSettings;

  if (domain || status !== undefined) {
    let newStatus: string | undefined;
    if (status !== undefined) {
      newStatus = status ? 'ACTIVE' : 'INACTIVE';
    }

    await patchLandingPageDomainApi({
      merchantCode,
      landingPageId,
      domain,
      status: newStatus,
    });
  }

  delete generalSettings.subdomain;

  return axiosInstance.put<FormValuesProps>(`${BASE_URL}/component-settings/landing-pages/${componentConfigId}`, componentSettings);
}

//This endpoint also allows status and path patching
async function patchLandingPageDomainApi({
  merchantCode,
  landingPageId,
  domain,
  status,
}: {
  merchantCode: string;
  landingPageId: string | undefined;
  domain?: string;
  status?: string;
}) {
  const body = {
    ...(domain && {
      subdomain: domain,
    }),
    ...(status && {
      status,
    }),
  };

  await axiosInstance.patch<FormValuesProps>(`${BASE_URL}/component-settings/landing-pages/${landingPageId}/merchant/${merchantCode}`, body);
}

export {
  sendTestEmailApi,
  getActiveThemeForMerchantApi,
  saveThemeForMerchantApi,
  saveImageForMerchantApi,
  getListOfMerchantLandingPages,
  saveComponentSettingsApi,
  getDefaultComponentSettings,
  getSettingsById,
  updateComponentSettingsApi,
  getForMerchantThemeApi,
  getActivePortalConfigForMerchantApi,
};